/**
 * HTTP client for static frontend.
 * Uses centralized API configuration.
 */

import type { RequestOptions } from '../../shared/types';
import {
  DEFAULT_BASE_DELAY_MS,
  DEFAULT_MAX_RETRIES,
  RETRYABLE_STATUSES,
  delay,
  extractErrorMessage,
  getCsrfTokenFromMeta,
  getRetryDelay,
  isMutatingMethod,
  readJsonResponse,
  setCsrfToken,
  unwrapSuccessEnvelope,
} from '../../shared/utils/http-common';

export class HTTPError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public statusText: string,
    public endpoint: string,
    public responseData?: unknown
  ) {
    super(message);
    this.name = 'HTTPError';
  }
}

const MAX_RETRIES = DEFAULT_MAX_RETRIES;
const BASE_DELAY_MS = DEFAULT_BASE_DELAY_MS;

import { getApiUrl } from '../config/api';
import { generateUuid } from '@shared/utils/uuid';

function getCustomerRef(): string | null {
  // Try 1: Get customer_ref from sessionStorage (where login stores it)
  try {
    const sessionContextRaw = sessionStorage.getItem('enfa_session_context');
    if (sessionContextRaw) {
      const sessionContext = JSON.parse(sessionContextRaw);
      if (sessionContext?.customer_ref) {
        return sessionContext.customer_ref;
      }
    }
  } catch (error) {
    console.warn('Failed to get customer_ref from sessionStorage:', error);
  }
  
  // Try 2: Get customer_ref from server-provided session data (window.APP_SESSION)
  try {
    if (window.APP_SESSION?.customer?.customer_ref) {
      return window.APP_SESSION.customer.customer_ref;
    }
  } catch (error) {
    console.warn('Failed to get customer_ref from APP_SESSION:', error);
  }
  
  return null;
}

export function resolveFullUrl(endpoint: string): string {
  // If endpoint is already a full URL, return it
  if (endpoint.startsWith('http')) {
    return endpoint;
  }

  // Client-specific endpoints that need session cookies (must stay relative to current host)
  // These endpoints need the customer_ref from Flask session
  const clientOnlyEndpoints = [
    '/api/client-auth/login',
    '/api/client-auth/register',
    '/api/client-auth/logout',
    '/api/client-auth/me',
    '/api/customer/',
    '/api/shortcuts',
    '/api/feedback/questions',
    '/api/sessions/me',
    '/api/sessions/open',
    '/api/v1/dining-sessions/open',
    '/api/v1/dining-sessions/',
    '/api/sessions/table-context',
    '/api/sessions/validate-and-rehydrate',
  ];

  const isClientOnly = clientOnlyEndpoints.some(path =>
    endpoint.startsWith(path) || endpoint === path
  );

  if (isClientOnly) {
    // Keep relative to current host - session cookies need same origin
    return endpoint;
  }

  // Use centralized API configuration - auto-detect service from path prefix
  let service: 'api' | 'employees' | 'client' = 'client';

  if (endpoint.startsWith('/api/')) {
    service = 'api';
  } else if (endpoint.startsWith('/employees/')) {
    service = 'employees';
  }

  const baseUrl = getApiUrl(service);

  // If config returns a base URL, prepend it. Otherwise return as is (relative).
  return baseUrl ? `${baseUrl}${endpoint}` : endpoint;
}

function applyCsrfTokenToMeta(token?: string | null): string | null {
  const normalized = String(token || '').trim();
  if (!normalized) return null;

  setCsrfToken(normalized);
  
  return normalized;
}

async function refreshCsrfToken(): Promise<string | null> {
  // Re-read CSRF token from current page HTML or fallback API endpoint.
  try {
    const pageResponse = await fetch(window.location.href, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'text/html' },
    });
    if (pageResponse.ok) {
      const html = await pageResponse.text();
      const match = html.match(/<meta\s+name=["']csrf-token["']\s+content=["']([^"']+)["']/i);
      if (match?.[1]) return applyCsrfTokenToMeta(match[1]);
    }
  } catch {
    // Ignore HTML fetch failure and try fallback endpoint
  }

  try {
    const csrfResponse = await fetch('/api/client-auth/csrf', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (csrfResponse.ok) {
      const data = await csrfResponse.json();
      const token = data?.data?.csrf_token || data?.csrf_token;
      if (token) return applyCsrfTokenToMeta(token);
    }
  } catch {
    // Ignore fallback failure
  }

  return null;
}

function isCsrfError(statusCode: number, message: string): boolean {
  if (statusCode !== 400 && statusCode !== 403) return false;
  return String(message || '').toLowerCase().includes('csrf');
}



export async function requestJSON<TResponse = unknown, TBody = unknown>(
  endpoint: string,
  { method = 'GET', body, headers = {} }: RequestOptions<TBody> = {}
): Promise<TResponse> {
  const fullUrl = resolveFullUrl(endpoint);

  // Add CSRF token for non-GET requests (Canon 12.3 AGENTS.md)
  let csrfToken = getCsrfTokenFromMeta();
  const isMutating = isMutatingMethod(method);
  let csrfRefreshed = false;

  if (isMutating && !csrfToken) {
    csrfToken = await refreshCsrfToken();
    if (!csrfToken) {
      throw new Error('CSRF token missing for mutating request');
    }
  }

  let lastError: Error | null = null;
  let statusCode = 0;
  let statusText = '';
  let responseData: unknown;

  // Pre-generate idempotency key outside retry loop so all attempts share the same key.
  const idempotencyKey = (isMutating && !headers['X-Idempotency-Key'] && !headers['Idempotency-Key'] &&
    !headers['x-idempotency-key'] && !headers['idempotency-key'])
    ? generateUuid()
    : null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const requestHeaders: Record<string, string> =
        body ? { 'Content-Type': 'application/json', ...headers } : { ...headers };

      if (csrfToken && isMutating) {
        requestHeaders['X-CSRFToken'] = csrfToken;
      }

      if (idempotencyKey) {
        requestHeaders['X-Idempotency-Key'] = idempotencyKey;
      }

      // Add customer_ref header for authenticated API calls
      const customerRef = getCustomerRef();
      if (customerRef) {
        requestHeaders['X-ENFA-CUSTOMER-REF'] = customerRef;
      }

      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });

      statusCode = response.status;
      statusText = response.statusText;

      responseData = await readJsonResponse(response);

      if (response.ok) {
        // Automatic unwrapping of enfa-api standard envelope
        const unwrapped = unwrapSuccessEnvelope<TResponse>(responseData);
        if (unwrapped !== null) return unwrapped;
        return responseData as TResponse;
      }

      const message = extractErrorMessage(responseData);
      const isRetryable = RETRYABLE_STATUSES.includes(statusCode);

      if (isMutating && isCsrfError(statusCode, message) && !csrfRefreshed) {
        csrfRefreshed = true;
        const renewedToken = await refreshCsrfToken();
        if (renewedToken) {
          csrfToken = renewedToken;
          continue;
        }
      }

      // Handle 401 Unauthorized — do NOT redirect; let callers decide.
      // Session rehydration authority lives in bootstrap flow, not in transport layer.
      if (statusCode === 401 && !endpoint.includes('validate-and-rehydrate')) {
        throw new HTTPError(message || 'Unauthorized', 401, statusText, fullUrl, responseData);
      }

      if (isRetryable && attempt < MAX_RETRIES) {
        lastError = new HTTPError(message, statusCode, statusText, fullUrl, responseData);
        await delay(getRetryDelay(attempt, BASE_DELAY_MS));
        continue;
      }

      throw new HTTPError(
        message || 'Network error',
        statusCode || 0,
        statusText || 'Network Error',
        fullUrl,
        responseData
      );
    } catch (error) {
      if (error instanceof HTTPError) {
        throw error;
      }
      lastError = error instanceof Error ? error : new Error('Unknown error');
      if (attempt < MAX_RETRIES) {
        await delay(getRetryDelay(attempt, BASE_DELAY_MS));
        continue;
      }
      throw new HTTPError(
        lastError.message || 'Network error',
        statusCode || 0,
        statusText || 'Network Error',
        fullUrl,
        responseData
      );
    }
  }

  if (lastError) throw lastError;
  throw new Error('Unknown network error');
}

export async function requestBlob(
  endpoint: string,
  { method = 'GET', body, headers = {} }: RequestOptions<unknown> = {}
): Promise<Blob> {
  const fullUrl = resolveFullUrl(endpoint);

  // Add CSRF token for non-GET requests
  const csrfToken = getCsrfTokenFromMeta();
  const isMutating = isMutatingMethod(method);

  let lastError: Error | null = null;

  // Pre-generate idempotency key outside retry loop so all attempts share the same key.
  const idempotencyKey = (isMutating && !headers['X-Idempotency-Key'] && !headers['Idempotency-Key'] &&
    !headers['x-idempotency-key'] && !headers['idempotency-key'])
    ? generateUuid()
    : null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const requestHeaders: Record<string, string> = { ...headers };

      if (csrfToken && isMutating) {
        requestHeaders['X-CSRFToken'] = csrfToken;
      }

      if (idempotencyKey) {
        requestHeaders['X-Idempotency-Key'] = idempotencyKey;
      }

      // Add customer_ref header for authenticated API calls
      const customerRef = getCustomerRef();
      if (customerRef) {
        requestHeaders['X-ENFA-CUSTOMER-REF'] = customerRef;
      }

      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new HTTPError(
          `Request failed with status ${response.status}`,
          response.status,
          response.statusText,
          fullUrl,
          errorText
        );
      }

      return await response.blob();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      if (attempt < MAX_RETRIES) {
        await delay(getRetryDelay(attempt, BASE_DELAY_MS));
        continue;
      }
      throw lastError;
    }
  }
  throw new Error('Unknown network error');
}

export async function getOrderHistory(): Promise<{ orders: any[] }> {
  return requestJSON<{ orders: any[] }>('/api/customer/orders');
}
